/* editor-fork/variant-profile.jsx — VariantProfile.v1, shown as the email it describes.

   The backend profile is 13 region_policies × 9 fields plus layout rules,
   digests and an authority ladder. Rendered as a table that is ~120 cells of
   snake_case — unreadable, and it throws away the one thing that makes the
   document legible: region_policies are in DOCUMENT ORDER. The profile is a
   picture of the email.

   So the centrepiece is a build stack: the email top to bottom at its real
   600px proportion, every region a band, and `disposition` carried by the
   band's FORM rather than a column of words —

     metadata  lives outside the body (subject, preheader) → envelope lines
     ship      a real band in the paper; `image` hatched, `live` as type
     omit      a ghost slot, deliberately and visibly absent
     inject    ESP-owned, below the fold line, in the ESP tint

   Everything else hangs off a band: layout rules pin to the region they
   constrain, reason codes and evidence sit in the inspector, and the authority
   ladder is shown once with the rungs this profile actually exercised. Nothing
   from the JSON is dropped — it is just addressed by pointing at a band instead
   of reading a row. */

(function () {
  const { useState, useMemo } = React;

  const ACRO = { esp: 'ESP', cta: 'CTA', amp: 'AMP', sfmc: 'SFMC', html: 'HTML' };
  const human = (s) => String(s || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
    .split(' ').map((w) => ACRO[w.toLowerCase()] || w).join(' ');
  /* The enum values are for machines; the inspector reads them out in words. */
  const BEHAV = {
    'metadata-only': 'not in the body',
    'absent': 'not rendered',
    'fluid-600-to-320': 'fluid, 600 → 320px',
    'centered-live-text': 'centred live text',
    'right-aligned-live-text': 'right-aligned live text',
    'esp-owned': 'the ESP decides',
    'preserve-asset': 'asset kept as-is',
    'preserve-live-color': 'colours kept as authored',
  };
  const behav = (v) => BEHAV[v] || String(v || '').replace(/-/g, ' ');
  const short = (d) => (d || '').slice(0, 7);

  /* disposition → the band's form. owner → its tint. */
  const DISP = {
    ship: { l: 'ships', cls: 'ship', why: 'Built into the email body' },
    metadata: { l: 'metadata', cls: 'meta', why: 'Carried as a header, never in the body' },
    omit: { l: 'omitted', cls: 'omit', why: 'Deliberately absent — the dev reference omits it' },
    inject: { l: 'ESP injects', cls: 'esp', why: 'The ESP owns this at send time' },
  };
  const OWNER = { builder: 'builder', esp: 'ESP', metadata: 'header', none: 'nobody' };

  /* Mock body per role so a band reads as the thing it is, at a glance. */
  const SHAPE = {
    campaign_banner: { h: 104, kind: 'img' },
    headline: { h: 40, kind: 'head' },
    body_copy: { h: 58, kind: 'body' },
    promotion_terms: { h: 76, kind: 'img' },
    linked_cta_panel: { h: 56, kind: 'cta' },
    legal_note: { h: 30, kind: 'small' },
    legal_copy: { h: 38, kind: 'small' },
    job_code: { h: 20, kind: 'right' },
  };

  function BandArt({ role, kind }) {
    if (kind === 'img') return <span className="vpf-art vpf-hatch"><i>{role === 'campaign_banner' ? 'banner image' : 'terms image'}</i></span>;
    if (kind === 'cta') return <span className="vpf-art vpf-cta"><i>linked image panel</i></span>;
    if (kind === 'head') return <span className="vpf-art vpf-lines c"><b style={{ width: '62%' }}></b></span>;
    if (kind === 'body') return <span className="vpf-art vpf-lines c"><b style={{ width: '88%' }}></b><b style={{ width: '80%' }}></b><b style={{ width: '54%' }}></b></span>;
    if (kind === 'small') return <span className="vpf-art vpf-lines sm"><b style={{ width: '96%' }}></b><b style={{ width: '72%' }}></b></span>;
    if (kind === 'right') return <span className="vpf-art vpf-lines sm r"><b style={{ width: '28%' }}></b></span>;
    return <span className="vpf-art"></span>;
  }

  function VariantProfileV1({ p, proposed }) {
    const [sel, setSel] = useState(null);
    const [showLadder, setShowLadder] = useState(false);
    const [raw, setRaw] = useState(false);

    const regions = p.region_policies || [];
    const rules = p.layout_rules || [];
    const rulesFor = (role) => rules.filter((r) => r.semantic_role === role);

    const meta = regions.filter((r) => r.disposition === 'metadata');
    const body = regions.filter((r) => r.disposition === 'ship' || r.disposition === 'omit');
    const injected = regions.filter((r) => r.disposition === 'inject');

    const counts = useMemo(() => {
      const c = {};
      regions.forEach((r) => { c[r.disposition] = (c[r.disposition] || 0) + 1; });
      return c;
    }, [regions]);
    const used = useMemo(() => {
      const c = {};
      regions.forEach((r) => { c[r.authority_selected] = (c[r.authority_selected] || 0) + 1; });
      return c;
    }, [regions]);

    const cur = regions.find((r) => r.semantic_role === sel) || null;

    const Band = ({ r }) => {
      const d = DISP[r.disposition] || DISP.ship;
      const sh = SHAPE[r.semantic_role] || { h: 34, kind: null };
      const on = sel === r.semantic_role;
      const rr = rulesFor(r.semantic_role);
      const omit = r.disposition === 'omit';
      return (
        <button type="button" className={'vpf-band ' + d.cls + (on ? ' on' : '')}
          aria-pressed={on}
          style={omit ? null : { minHeight: sh.h }}
          onClick={() => setSel(on ? null : r.semantic_role)}
          title={human(r.semantic_role) + ' — ' + d.why}>
          <span className="vpf-band-tag">
            {human(r.semantic_role)}
            {r.implementation_mode && <i className={'vpf-mode ' + r.implementation_mode}>{r.implementation_mode}</i>}
            {omit && <i className="vpf-mode omit">omitted</i>}
            {!!rr.length && <i className="vpf-pin" title={rr.map((x) => x.property + ' = ' + x.typed_value.value).join(' · ')}>{rr.length}</i>}
          </span>
          {!omit && <BandArt role={r.semantic_role} kind={sh.kind} />}
          {r.responsive_behavior === 'fluid-600-to-320' && <span className="vpf-fluid" title="Fluid 600 → 320">600→320</span>}
        </button>);
    };

    return (
      <div className={'card v-section vpf' + (proposed ? ' prop' : '')}>
        <div className="sec-head">
          <span className="sec-title">{proposed ? 'Proposed layout' : 'Profile'}{proposed && <i className="vpf-prop-tag">not recorded yet</i>}</span>
          <span className="vpf-idline">
            <code>{p.profile_id}</code>
            <i className="vpf-schema" title={'schema ' + p.schema_version}>{p.schema_version}</i>
          </span>
        </div>

        <div className="vpf-scope">
          {[['client', p.scope?.client_id], ['sub-brand', p.scope?.sub_brand_id], ['variant', p.scope?.variant_id], ['esp', p.scope?.esp_id], ['workspace', p.scope?.workspace_id]].map(([k, v]) =>
            <span key={k} className="vpf-sc"><i>{k}</i>{v}</span>)}
          <span className="vpf-grow"></span>
          <span className="vpf-sc num"><i>container</i>{p.container_width}px</span>
          <span className="vpf-sc num"><i>family</i>{p.message_family}</span>
        </div>

        <div className="vpf-main">
          {/* ── the stack: the profile as the email it describes ── */}
          <div className="vpf-stack">
            <div className="vpf-legend">
              {['ship', 'metadata', 'omit', 'inject'].map((k) =>
                <span key={k} className={'vpf-lg ' + DISP[k].cls} title={DISP[k].why}><i></i>{DISP[k].l}<b>{counts[k] || 0}</b></span>)}
            </div>

            {!!meta.length &&
              <div className="vpf-env">
                {meta.map((r) => {
                  const on = sel === r.semantic_role;
                  return (
                    <button type="button" key={r.policy_id} className={'vpf-envline' + (on ? ' on' : '')}
                      aria-pressed={on}
                      onClick={() => setSel(on ? null : r.semantic_role)} title={human(r.semantic_role) + ' — carried as a header, never in the body'}>
                      <i>{human(r.semantic_role)}</i>
                      <span className="vpf-envbar"></span>
                      <em>metadata</em>
                    </button>);
                })}
              </div>}

            <div className="vpf-paper">
              {body.map((r) => <React.Fragment key={r.policy_id}>{Band({ r })}</React.Fragment>)}
            </div>

            {!!injected.length &&
              <div className="vpf-inject">
                <span className="vpf-seam"><i></i>send time<i></i></span>
                {injected.map((r) => {
                  const on = sel === r.semantic_role;
                  return (
                    <button type="button" key={r.policy_id} className={'vpf-band esp' + (on ? ' on' : '')}
                      aria-pressed={on}
                      onClick={() => setSel(on ? null : r.semantic_role)} title="The ESP owns this at send time">
                      <span className="vpf-band-tag">{human(r.semantic_role)}<i className="vpf-mode esp">injected</i></span>
                      <span className="vpf-own">{(p.wrapper_ownership || []).join(' · ')}</span>
                    </button>);
                })}
              </div>}
          </div>

          {/* ── inspector: one region at a time, in full ── */}
          <div className="vpf-side">
            {!cur ?
              <div className="vpf-hint">
                <I.layers size={15} />
                <span><b>Click a band</b> to see the policy behind it — owner, renderer recipe, responsive and dark-mode behaviour, the authority that decided it and the evidence it came from.</span>
              </div> :
              <div className="vpf-insp">
                <div className="vpf-insp-h">
                  <span className={'vpf-dot ' + (DISP[cur.disposition] || DISP.ship).cls}></span>
                  <b>{human(cur.semantic_role)}</b>
                  <span className="vpf-grow"></span>
                  <button className="vpf-x" onClick={() => setSel(null)} title="Close region details" aria-label="Close region details"><I.x size={13} /></button>
                </div>
                <div className="vpf-kv"><i>disposition</i><span>{(DISP[cur.disposition] || {}).l}<em>{(DISP[cur.disposition] || {}).why}</em></span></div>
                <div className="vpf-kv"><i>owner</i><span>{OWNER[cur.owner] || cur.owner}</span></div>
                {cur.implementation_mode && <div className="vpf-kv"><i>built as</i><span>{cur.implementation_mode === 'image' ? 'image' : 'live text'}</span></div>}
                <div className="vpf-kv"><i>recipe</i><span><code>{cur.renderer_recipe}</code></span></div>
                <div className="vpf-kv"><i>responsive</i><span>{behav(cur.responsive_behavior)}<em title="responsive_behavior"><code>{cur.responsive_behavior}</code></em></span></div>
                <div className="vpf-kv"><i>dark mode</i><span>{behav(cur.dark_mode_behavior)}<em title="dark_mode_behavior"><code>{cur.dark_mode_behavior}</code></em></span></div>

                <div className="vpf-auth">
                  <span className="vpf-auth-h">decided by</span>
                  <span className="vpf-auth-v">{human(cur.authority_selected).toLowerCase()}</span>
                  <code className="vpf-reason" title="reason_code">{cur.reason_code}</code>
                </div>

                {!!rulesFor(cur.semantic_role).length &&
                  <div className="vpf-rules">
                    <span className="vpf-rules-h">layout rules</span>
                    {rulesFor(cur.semantic_role).map((r) =>
                      <div className="vpf-rule" key={r.rule_id}>
                        <span>{human(r.property).toLowerCase()}</span>
                        <b>{r.typed_value.value}{r.property !== 'font_size' && r.property !== 'line_height' ? 'px' : 'px'}</b>
                        <code title={r.rule_id}>{r.rule_id.split('.').slice(-1)[0]}</code>
                      </div>)}
                  </div>}

                <div className="vpf-ev">
                  <span className="vpf-ev-h">evidence</span>
                  {(cur.evidence_refs || []).map((d) => <code key={d} title={d}>{short(d)}</code>)}
                  {!cur.correction_receipt_digest && <span className="vpf-nocorr" title="No human has overridden this policy">no corrections</span>}
                </div>
              </div>}

            {/* ── the ladder: why anything resolved the way it did ── */}
            <div className="vpf-ladder">
              <button className="vpf-ladder-h" aria-expanded={showLadder} onClick={() => setShowLadder((v) => !v)}>
                <I.chevron size={11} style={{ transform: showLadder ? 'rotate(90deg)' : 'none' }} />
                Authority precedence
                <span className="vpf-grow"></span>
                <span className="vpf-lmini">{Object.keys(used).length} of {(p.authority_precedence || []).length} in use</span>
              </button>
              {showLadder &&
                <div className="vpf-rungs">
                  {(p.authority_precedence || []).map((a, i) => {
                    const n = used[a] || 0;
                    return (
                      <div key={a} className={'vpf-rung' + (n ? ' live' : '')}>
                        <i>{i + 1}</i>
                        <span>{human(a).toLowerCase()}</span>
                        {n ? <b>{n} region{n > 1 ? 's' : ''}</b> : <em>not exercised</em>}
                      </div>);
                  })}
                  <p className="vpf-note">Higher rungs win.{!used.human_override && ' A human override would outrank every rule below it — none exists on this profile.'}</p>
                </div>}
            </div>
          </div>
        </div>

        <div className="vpf-foot">
          <span className="vpf-sr" title={(p.source_reference_digests || []).join('\n')}>
            <I.doc size={11} /> {(p.source_reference_digests || []).length} source references
            {(p.source_reference_digests || []).map((d) => <code key={d}>{short(d)}</code>)}
          </span>
          <span className="vpf-grow"></span>
          <button className="vpf-rawbtn" aria-expanded={raw} onClick={() => setRaw((v) => !v)}><I.code size={11} /> {raw ? 'Hide' : 'Show'} raw profile</button>
        </div>
        {raw && <pre className="vpf-raw">{JSON.stringify(p, null, 2)}</pre>}
      </div>);
  }

  window.VariantProfileV1 = VariantProfileV1;
})();
